import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosRetry, { IAxiosRetryConfig } from 'axios-retry';
import { Dispatch } from 'redux';
import config from '../../../../app.config.json';
import { setLoggedIn } from '../reducers/user';
import logging from '../services/logging';
import { NetworkContext } from '../types/network';
import { getAuthSignature } from '../utils/auth';
import getUserAgent from '../utils/getUserAgent';
import logout from '../utils/logout';

export const API_URL = process?.env?.API_URL ? process.env.API_URL : config.apiUrl;

const RETRY_DELAY = 1000;
export const RETRY_CONFIG: IAxiosRetryConfig = {
  retries: process?.env?.API_URL ? 1 : 5,
  // increment each retry request by another second
  retryDelay: (retryCount) => retryCount * RETRY_DELAY,
  // allows retry whenever cloudflare returns 429 error for too many requests
  retryCondition: (error) => {
    return (error && error.response && [429, 503, 504].includes(error.response.status)) || false;
  },
};

if (!process?.env?.JEST_WORKER_ID) {
  axiosRetry(axios, RETRY_CONFIG);
}

const axiosInstance = async <T = any>(
  requestConfig: AxiosRequestConfig,
  dispatch: Dispatch | undefined,
  ctx?: NetworkContext,
  retried?: boolean,
): Promise<AxiosResponse<T>> => {
  const url = axios.getUri(requestConfig);
  const { deviceId, signature, token } = await getAuthSignature(url, ctx, dispatch);
  const userAgent = getUserAgent(ctx);

  logging.logCrumb({
    message: 'request',
    metaData: {
      url,
      method: requestConfig.method,
      authExpires: token?.authTokenExpires,
      loggedIn: token?.loggedIn,
    },
  });

  if (!token?.authToken) {
    throw new Error('Missing auth token');
  }

  if (dispatch) {
    dispatch(setLoggedIn(token.loggedIn));
  }

  try {
    const result = await axios({
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        ...userAgent,
        'X-API-Client': config.clientId,
        'X-API-Device': deviceId,
        'X-API-Signature': signature,
      },
    });

    return result;
  } catch (error: any) {
    if (error.response) {
      logging.logError(new Error(`API error ${error.response.status} on ${url}`));

      if ([401, 403].includes(error.response.status)) {
        await logout(ctx);
      }
    }

    if ([401, 403].includes(error.response?.status) && !retried) {
      return axiosInstance(requestConfig, dispatch, ctx, true);
    }
    throw error;
  }
};

export default axiosInstance;
